import React, { useEffect, useState } from 'react';
import {
  Box, Button, Chip, Popover, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import { EventOutlined as CalendarIcon } from '@mui/icons-material';

import { callState, quickTimes, toInput, fromInput } from './callTime';
import { status as statusTokens } from '../../theme';

/**
 * "I can talk Tuesday at 12."
 *
 * The most perishable fact in the pipeline, and the one the spreadsheet
 * could not hold at all — it lived in a text thread on somebody's phone,
 * which is how calls got missed. Two taps to record it: the answers
 * people actually give are buttons, and anything else is a normal
 * date-and-time box.
 *
 * The chip IS the button. A scheduled call has to be visible without
 * opening anything, and an empty one has to invite the booking, so the
 * same control does both jobs rather than a label plus an edit pencil.
 */

const TONE: Record<string, { color: string; solid: boolean }> = {
  overdue: { color: statusTokens.danger, solid: true },
  now: { color: statusTokens.danger, solid: true },
  soon: { color: statusTokens.warn, solid: true },
  today: { color: statusTokens.warn, solid: false },
  later: { color: statusTokens.info, solid: false },
};

const ScheduleCall: React.FC<{
  scheduledCallAt?: string | null;
  disabled?: boolean;
  /** The interview happened, so a time in the past is not a missed call. */
  held?: boolean;
  onSave: (iso: string | null) => void | Promise<void>;
  /** `chip` for a page header, `text` for a row in a table. */
  variant?: 'chip' | 'text';
}> = ({
  scheduledCallAt, disabled, held, onSave, variant = 'chip',
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [value, setValue] = useState(toInput(scheduledCallAt));

  useEffect(() => { setValue(toInput(scheduledCallAt)); }, [scheduledCallAt, anchor]);

  const state = callState(scheduledCallAt, new Date(), held);
  const tone = TONE[state.urgency];

  const commit = async (next: string) => {
    setAnchor(null);
    await onSave(fromInput(next));
  };

  const label = state.urgency === 'none' ? 'Schedule a call' : state.words;

  return (
    <>
      {variant === 'chip' ? (
        <Tooltip title={state.urgency === 'none' ? 'When did they say they could talk?' : 'Change or cancel'}>
          <Chip
            icon={<CalendarIcon sx={{ fontSize: 16 }} />}
            label={label}
            size="small"
            onClick={disabled ? undefined : (e) => setAnchor(e.currentTarget)}
            variant={tone?.solid ? 'filled' : 'outlined'}
            sx={{
              height: 28,
              fontWeight: tone?.solid ? 700 : 500,
              ...(tone ? {
                borderColor: tone.color,
                ...(tone.solid
                  ? { bgcolor: tone.color, color: '#fff', '& .MuiChip-icon': { color: '#fff' } }
                  : { color: tone.color, '& .MuiChip-icon': { color: tone.color } }),
              } : {}),
            }}
          />
        </Tooltip>
      ) : (
        <Box
          role="button"
          tabIndex={0}
          onClick={disabled ? undefined : (e) => setAnchor(e.currentTarget as HTMLElement)}
          sx={{
            cursor: disabled ? 'default' : 'pointer',
            color: tone?.color || 'text.disabled',
            fontWeight: tone?.solid ? 700 : 400,
            fontSize: 12,
          }}
        >
          {state.urgency === 'none' ? '—' : state.words}
        </Box>
      )}

      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, width: 280 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>When can they talk?</Typography>

          <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: 2 }}>
            {quickTimes().map((quick) => (
              <Chip
                key={quick.label}
                size="small"
                variant="outlined"
                label={quick.label}
                onClick={() => commit(quick.value)}
              />
            ))}
          </Stack>

          <TextField
            size="small"
            type="datetime-local"
            fullWidth
            value={value}
            onChange={(e) => setValue(e.target.value)}
            inputProps={{ 'aria-label': 'Call date and time' }}
          />

          <Stack direction="row" gap={1} sx={{ mt: 2 }}>
            <Button
              size="small"
              variant="contained"
              disableElevation
              disabled={!value}
              onClick={() => commit(value)}
            >
              Save
            </Button>
            {scheduledCallAt && (
              <Button size="small" color="inherit" onClick={() => commit('')}>
                Cancel the call
              </Button>
            )}
          </Stack>
        </Box>
      </Popover>
    </>
  );
};

export default ScheduleCall;
